import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, ArrowLeft, Loader2, Check, Copy, MessageCircle, Star, ClipboardCheck, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { waLink } from "../lib/img";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";

const inp = "mt-1 border-white/10 bg-white/5 text-right text-white";

export default function Deal() {
  const [tab, setTab] = useState("buyer");
  return (
    <main className="relative min-h-screen pt-28">
      <div className="absolute left-1/2 top-20 h-64 w-[700px] -translate-x-1/2 hero-glow blur-3xl" />
      <section className="relative mx-auto max-w-2xl px-5 lg:px-8">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-white"><ArrowLeft className="h-4 w-4 rotate-180" /> الرئيسية</Link>
        <div className="mt-4 flex items-center gap-2">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl brand-gradient text-[#052027]"><ShieldCheck className="h-5 w-5" /></span>
          <div>
            <h1 className="font-display text-2xl font-extrabold text-white">توثيق العمليات</h1>
            <p className="text-xs text-muted-foreground">إثبات العملية بين المشتري والمعلن — أساس تقييم المعلنين</p>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-2">
          <button data-testid="tab-buyer" onClick={() => setTab("buyer")} className={`flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold ${tab === "buyer" ? "btn-cyan" : "border border-white/10 text-muted-foreground"}`}><ClipboardCheck className="h-4 w-4" /> أنا مشترٍ (توثيق)</button>
          <button data-testid="tab-advertiser" onClick={() => setTab("advertiser")} className={`flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold ${tab === "advertiser" ? "btn-cyan" : "border border-white/10 text-muted-foreground"}`}><UserCheck className="h-4 w-4" /> أنا معلن (تأكيد)</button>
        </div>

        <div className="mt-6">{tab === "buyer" ? <BuyerFlow /> : <AdvertiserFlow />}</div>
        <div className="h-20" />
      </section>
    </main>
  );
}

function BuyerFlow() {
  const [f, setF] = useState({ advertiser_code: "", buyer_name: "", buyer_mobile: "", service_type: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const submit = async () => {
    if (!f.advertiser_code || !f.buyer_name || !f.buyer_mobile || !f.service_type) { toast.error("أكمل كل الحقول"); return; }
    setBusy(true);
    try { const r = await api.createDeal({ ...f, advertiser_code: f.advertiser_code.trim().toUpperCase() }); setDone(r); toast.success("تم توثيق العملية"); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر التوثيق"); }
    setBusy(false);
  };
  if (done) return (
    <div className="rounded-2xl border border-cyan/25 bg-cyan/5 p-6 text-center">
      <Check className="mx-auto h-10 w-10 text-emerald-400" />
      <h3 className="mt-3 font-display text-lg font-bold text-white">تم إنشاء رمز الجروب</h3>
      <p className="mt-1 text-sm text-muted-foreground">أعطِ هذا الرمز للمعلن ليؤكّد العملية من حسابه.</p>
      <div className="mt-4 flex items-center justify-center gap-2">
        <span data-testid="group-code-result" className="font-en text-2xl font-bold tracking-wider text-gold" dir="ltr">{done.group_code}</span>
        <button onClick={() => { navigator.clipboard?.writeText(done.group_code); toast.success("نُسخ"); }} className="text-muted-foreground hover:text-white"><Copy className="h-4 w-4" /></button>
      </div>
      <button onClick={() => { setDone(null); setF({ advertiser_code: "", buyer_name: "", buyer_mobile: "", service_type: "" }); }} className="mt-5 text-sm text-cyan hover:underline">توثيق عملية أخرى</button>
    </div>
  );
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
      <p className="mb-4 text-sm text-muted-foreground">هذا نموذج توثيق (ليس دفعًا). املأ بياناتك ونوع الخدمة، ثم اضغط «تمام» ليخرج لك رمز جروب تعطيه للمعلن.</p>
      <div className="grid gap-3">
        <div><Label className="text-xs text-white">رمز المعلن (العام)</Label><Input data-testid="deal-adv-code" value={f.advertiser_code} onChange={(e) => setF({ ...f, advertiser_code: e.target.value })} className={inp} dir="ltr" placeholder="ADV-xxxxxx-xxxxxx" /></div>
        <div><Label className="text-xs text-white">اسمك</Label><Input data-testid="deal-buyer-name" value={f.buyer_name} onChange={(e) => setF({ ...f, buyer_name: e.target.value })} className={inp} /></div>
        <div><Label className="text-xs text-white">رقم جوالك</Label><Input data-testid="deal-buyer-mobile" value={f.buyer_mobile} onChange={(e) => setF({ ...f, buyer_mobile: e.target.value })} className={inp} dir="ltr" placeholder="05xxxxxxxx" /></div>
        <div><Label className="text-xs text-white">نوع الخدمة</Label><Input data-testid="deal-service" value={f.service_type} onChange={(e) => setF({ ...f, service_type: e.target.value })} className={inp} placeholder="شراء سيارة / إصلاح / نقل / خدمة…" /></div>
        <Button data-testid="deal-submit" onClick={submit} disabled={busy} className="btn-cyan h-11 gap-2 rounded-xl font-semibold hover:bg-transparent">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} تمام (إتمام التوثيق)</Button>
      </div>
    </div>
  );
}

function AdvertiserFlow() {
  const [secret, setSecret] = useState("");
  const [acct, setAcct] = useState(null);
  const [busy, setBusy] = useState(false);
  const [conf, setConf] = useState({ group_code: "", note: "" });
  const login = async () => {
    if (!secret) { toast.error("أدخل رمزك الخاص"); return; }
    setBusy(true);
    try { const r = await api.advertiserLogin(secret.trim().toUpperCase()); setAcct(r); }
    catch (e) { toast.error(e?.response?.data?.detail || "رمز غير صحيح"); }
    setBusy(false);
  };
  const confirm = async (gc) => {
    const group_code = gc || conf.group_code;
    if (!group_code) { toast.error("أدخل رمز الجروب"); return; }
    if (!conf.note) { toast.error("اكتب ما تم إنجازه"); return; }
    setBusy(true);
    try { await api.confirmDeal({ secret: secret.trim().toUpperCase(), group_code: group_code.trim().toUpperCase(), note: conf.note }); toast.success("تم توثيق العملية وتحديث تقييمك"); setConf({ group_code: "", note: "" }); await login(); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر التأكيد"); }
    setBusy(false);
  };
  if (!acct) return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
      <p className="mb-4 text-sm text-muted-foreground">أدخل رمزك الخاص (السري) لعرض عملياتك وتأكيدها.</p>
      <div className="flex gap-2">
        <Input data-testid="adv-secret" value={secret} onChange={(e) => setSecret(e.target.value)} className="border-white/10 bg-white/5 text-white" dir="ltr" placeholder="الرمز الخاص" />
        <Button data-testid="adv-login-btn" onClick={login} disabled={busy} className="btn-cyan gap-2 rounded-xl px-6 font-semibold hover:bg-transparent">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserCheck className="h-4 w-4" />} دخول</Button>
      </div>
    </div>
  );
  const pending = (acct.deals || []).filter((d) => d.status === "pending");
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between rounded-2xl border border-gold/25 bg-amber-400/5 p-4">
        <div><p className="font-semibold text-white">{acct.label || "معلن"}</p><p className="font-en text-xs text-muted-foreground" dir="ltr">{acct.code}</p></div>
        <div className="text-left"><p className="flex items-center gap-1 text-lg font-bold text-gold"><Star className="h-4 w-4 fill-gold" /> {acct.rating}</p><p className="text-xs text-muted-foreground">{acct.uses} عملية موثّقة</p></div>
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
        <h3 className="mb-3 font-semibold text-white">تأكيد عملية برمز الجروب</h3>
        <div className="grid gap-3">
          <Input data-testid="conf-group-code" value={conf.group_code} onChange={(e) => setConf({ ...conf, group_code: e.target.value })} className="border-white/10 bg-white/5 text-white" dir="ltr" placeholder="GRP-xxxxx-xxxx" />
          <Textarea data-testid="conf-note" value={conf.note} onChange={(e) => setConf({ ...conf, note: e.target.value })} rows={3} className="border-white/10 bg-white/5 text-right text-white" placeholder="اكتب ما تم إنجازه: تم إعطاء…، تم بناء…، تم الإصلاح…" />
          <Button data-testid="conf-submit" onClick={() => confirm()} disabled={busy} className="btn-cyan h-11 gap-2 rounded-xl font-semibold hover:bg-transparent">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} موافق (توثيق العملية)</Button>
        </div>
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
        <h3 className="mb-3 font-semibold text-white">عملياتك ({acct.deals?.length || 0}) — {pending.length} بانتظار التأكيد</h3>
        <div className="space-y-2">
          {(acct.deals || []).map((d) => (
            <div key={d.id} className="rounded-lg border border-white/10 bg-white/[0.02] p-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-white">{d.service_type}</span>
                <span className={`rounded-md px-2 py-0.5 text-xs ${d.status === "confirmed" ? "bg-emerald-400/10 text-emerald-400" : "bg-amber-400/10 text-gold"}`}>{d.status === "confirmed" ? "موثّقة" : "بانتظار"}</span>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">{d.buyer_name} · <span className="font-en" dir="ltr">{d.buyer_mobile}</span> · <span className="font-en" dir="ltr">{d.group_code}</span></p>
              {d.note && <p className="mt-1 text-xs text-white/70">{d.note}</p>}
              <div className="mt-2 flex gap-2">
                {d.status === "pending" && <button onClick={() => setConf({ group_code: d.group_code, note: conf.note })} className="text-xs text-cyan hover:underline">اختيار للتأكيد</button>}
                {d.buyer_mobile && <a href={waLink(d.buyer_mobile, `بخصوص عمليتك: ${d.service_type}`)} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs text-emerald-400 hover:underline"><MessageCircle className="h-3 w-3" /> واتساب المشتري</a>}
              </div>
            </div>
          ))}
          {!acct.deals?.length && <p className="py-4 text-center text-xs text-muted-foreground">لا توجد عمليات بعد.</p>}
        </div>
      </div>
      <button onClick={() => { setAcct(null); setSecret(""); }} className="text-sm text-muted-foreground hover:text-white">خروج</button>
    </div>
  );
}
